import React, { useRef, useState } from "react";
import { CancelManager } from "../lib/cancelManager.ts";
import { Button, Container, Grid, TextField, Typography } from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { Dayjs } from "dayjs";
import {
  ExportProgressModal,
  ExportProgressStatus,
} from "./parts/ExportProgressModal.tsx";
import { exportOrganizationData } from "../usecases/exportOrganizationData.ts";
import { config, CsvEncoding } from "../lib/config.ts";
import { CsvEncodingSelect } from "./parts/CsvEncodingSelect.tsx";

export const OrganizationAppContainer: React.FC<{ token: string }> = ({
  token,
}) => {
  const [visibleModal, setVisibleModal] = useState<boolean>(false);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [downloadedCount, setDownloadedCount] = useState<number>(0);
  const [fetchedCount, setFetchedCount] = useState<number>(0);
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [exportProgressInfo, setExportProgressInfo] = useState<{
    status: ExportProgressStatus;
    message: string;
  }>({ status: "Export", message: "" });
  const exportGenRef = useRef<number>(0);

  const [createdStartDate, setCreatedStartDate] = useState<string>("");
  const [createdEndDate, setCreatedEndDate] = useState<string>("");
  const [updatedStartDate, setUpdatedStartDate] = useState<string>("");
  const [updatedEndDate, setUpdatedEndDate] = useState<string>("");

  const [orgNameKeyword, setOrgNameKeyword] = useState<string>("");

  const [csvEncoding, setCsvEncoding] = useState<CsvEncoding>(
    config.defaultCsvEncoding,
  );

  const handleDateChange =
    (setter: React.Dispatch<React.SetStateAction<string>>) =>
    (newValue: Dayjs | null) => {
      setter(newValue ? newValue.format("YYYY-MM-DD") : "");
    };

  const handleExport = async () => {
    if (isExporting) return;
    const gen = ++exportGenRef.current;
    setIsExporting(true);
    setTotalCount(0);
    setDownloadedCount(0);
    setFetchedCount(0);
    setExportProgressInfo({ status: "Export", message: "" });
    setVisibleModal(true);
    await exportOrganizationData({
      token,
      createdStartDate,
      createdEndDate,
      updatedStartDate,
      updatedEndDate,
      orgNameKeyword,
      onUpdateTotalCount: (count) => {
        if (exportGenRef.current === gen) setTotalCount(count);
      },
      onUpdateDownloadedCount: (count) => {
        if (exportGenRef.current === gen) setDownloadedCount(count);
      },
      onUpdateFetchedCount: (count) => {
        if (exportGenRef.current === gen) setFetchedCount(count);
      },
      onUpdateInfo: (status, message) => {
        if (exportGenRef.current === gen)
          setExportProgressInfo({ status, message });
      },
      csvEncoding,
    });
    if (exportGenRef.current === gen) setIsExporting(false);
  };

  return (
    <Container maxWidth="md">
      {/* 作成日・更新日（横並び） */}
      <Grid container spacing={2} sx={{ mt: 5 }}>
        <Grid item xs={6}>
          <Typography variant="body2" textAlign="left" sx={{ mb: 1 }}>
            作成日（期間指定）
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="開始日"
                  format="YYYY/MM/DD"
                  slotProps={{ calendarHeader: { format: "YYYY年MM月" } }}
                  value={createdStartDate ? dayjs(createdStartDate) : null}
                  onChange={handleDateChange(setCreatedStartDate)}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={6}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="終了日"
                  format="YYYY/MM/DD"
                  slotProps={{ calendarHeader: { format: "YYYY年MM月" } }}
                  value={createdEndDate ? dayjs(createdEndDate) : null}
                  onChange={handleDateChange(setCreatedEndDate)}
                />
              </LocalizationProvider>
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={6}>
          <Typography variant="body2" textAlign="left" sx={{ mb: 1 }}>
            更新日（期間指定）
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="開始日"
                  format="YYYY/MM/DD"
                  slotProps={{ calendarHeader: { format: "YYYY年MM月" } }}
                  value={updatedStartDate ? dayjs(updatedStartDate) : null}
                  onChange={handleDateChange(setUpdatedStartDate)}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={6}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="終了日"
                  format="YYYY/MM/DD"
                  slotProps={{ calendarHeader: { format: "YYYY年MM月" } }}
                  value={updatedEndDate ? dayjs(updatedEndDate) : null}
                  onChange={handleDateChange(setUpdatedEndDate)}
                />
              </LocalizationProvider>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mt: 3 }}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="組織名キーワード"
            variant="outlined"
            value={orgNameKeyword}
            onChange={(e) => setOrgNameKeyword(e.target.value)}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mt: 2 }}>
        <Grid item xs={12}>
          <CsvEncodingSelect value={csvEncoding} onChange={setCsvEncoding} />
        </Grid>
      </Grid>

      <Grid>
        <Button
          variant="contained"
          style={{ marginTop: "16px" }}
          onClick={handleExport}
          disabled={isExporting}
          sx={{
            backgroundColor: "#00897B",
            "&:hover": { backgroundColor: "#00695C" },
          }}
        >
          エクスポート実行
        </Button>
        <ExportProgressModal
          open={visibleModal}
          onClose={() => {
            CancelManager.getInstance().cancel();
            setIsExporting(false);
            setVisibleModal(false);
            setTotalCount(0);
            setDownloadedCount(0);
            setFetchedCount(0);
            setExportProgressInfo({ status: "Export", message: "" });
          }}
          totalCount={totalCount}
          downloadedCount={downloadedCount}
          fetchedCount={fetchedCount}
          progressInfo={exportProgressInfo}
        />
      </Grid>
    </Container>
  );
};
